/** Ordered checklist shown on `PersonasDiscoverProgress` while a discover run is in flight. */
export const PERSONA_DISCOVER_STEPS = [
  { id: "analyze", labelKey: "personas.progress.step1" },
  { id: "identify", labelKey: "personas.progress.step2" },
  { id: "estimate", labelKey: "personas.progress.step3" },
  { id: "map", labelKey: "personas.progress.step4" },
] as const;

/** Hold on Accepted/Rejected footer so Undo is readable before the carousel advances. */
export const PERSONA_ONBOARDING_DECISION_HOLD_MS = 900;

/** Cosmetic checklist on the Figma analyzing beat (waits on data + min duration). */
export const PERSONA_ONBOARDING_ANALYZE_STEPS = [
  { id: "analyze", labelKey: "personas.onboarding.analyzing.step1" },
  { id: "identify", labelKey: "personas.onboarding.analyzing.step2" },
  { id: "estimate", labelKey: "personas.onboarding.analyzing.step3" },
  { id: "map", labelKey: "personas.onboarding.analyzing.step4" },
] as const;

/** Minimum analyzing beat duration so the transition never feels like a flash. */
export const PERSONA_ONBOARDING_ANALYZE_MIN_MS = 1600;

/** How often the analyzing checklist advances — purely cosmetic. */
export const PERSONA_ONBOARDING_ANALYZE_STEP_MS = 520;

/** Share-of-spend % thresholds for the "value tier" filter (B4) — High/Medium/Low. */
export const PERSONA_VALUE_TIER_HIGH_MIN_PERCENT = 15;
export const PERSONA_VALUE_TIER_MEDIUM_MIN_PERCENT = 5;

/** Sort dropdown options for the Library grid (B4), in display order. `label` is the
 * i18n `defaultValue` fallback — keep in sync with `frontend/web/src/locales/en.json`. */
export const PERSONA_SORT_OPTIONS = [
  { value: "shareOfSpend", labelKey: "personas.filters.sortShareOfSpend", label: "Sort: Share of Spend" },
  { value: "mostUsed", labelKey: "personas.filters.sortMostUsed", label: "Sort: Most used" },
  { value: "recentlyAdded", labelKey: "personas.filters.sortRecentlyAdded", label: "Sort: Recently added" },
] as const;

/** Value-tier dropdown options for the Library grid (B4), in display order. `label` is the
 * i18n `defaultValue` fallback — keep in sync with `frontend/web/src/locales/en.json`. */
export const PERSONA_VALUE_TIER_OPTIONS = [
  { value: "high", labelKey: "personas.filters.valueTierHigh", label: "High value" },
  { value: "medium", labelKey: "personas.filters.valueTierMedium", label: "Medium value" },
  { value: "low", labelKey: "personas.filters.valueTierLow", label: "Low value" },
] as const

/**
 * Status dropdown options for the Library. REJECTED is deliberately absent — rejected
 * rows enter the list through the show-rejected switch, not through this filter.
 */
export const PERSONA_STATUS_FILTER_OPTIONS = [
  { value: "ACTIVE", labelKey: "personas.status.approved", label: "Approved" },
  { value: "SUGGESTED", labelKey: "personas.status.new", label: "New" },
] as const

/** First-paint entrance stagger, shared by the Library's table rows and its card meters so both
 * views read as the same wave. Capped so the tail of a long list isn't left waiting. */
export const PERSONA_ENTRANCE_STAGGER_MS = 30
export const PERSONA_ENTRANCE_STAGGER_CAP = 20

/** Length of the spend meter's grow-in. Mirrors the `.persona-spend-fill` keyframe in
 * `styles/globals.css`: the percent readout counts up over the same window, so a meter at
 * two-thirds width always reads two-thirds of its final number. */
export const PERSONA_SPEND_FILL_DURATION_MS = 400

/** Holds the drawer's meter until the sheet has finished sliding in (200ms, `sheetVariants`
 * in `packages/ui`) so the grow-in isn't competing with the panel's own transform. */
export const PERSONA_DRAWER_SPEND_FILL_DELAY_MS = 200

/** Persisted view preference for the Library body (table vs. cards). */
export const PERSONA_VIEW_MODE_STORAGE_KEY = "nexus_personas_view_mode"

/**
 * Banner dismissals are keyed by persona id so a later batch re-opens the banner,
 * and live in `sessionStorage` so dismissing is a "not now", not a permanent opt-out.
 */
export const PERSONA_BANNER_DISMISSED_STORAGE_KEY = "nexus_personas_new_banner_dismissed";
